import type * as Types from '../types.generated';

import { gql } from '@oneaudi/onegraph-client';
import * as Apollo from '@apollo/client';
export type CustomizerMainQueryVariables = Types.Exact<{
  carlineInput: Types.ConfiguredCarByCarlineInput;
  identifier: Types.CarlineIdentifierInput;
}>;

export type CustomizerMainQuery = { __typename?: 'Query' } & {
  configuredCarByCarline: { __typename?: 'ConfiguredCar' } & Pick<
    Types.ConfiguredCar,
    'idString'
  > & {
      id: { __typename?: 'ConfiguredCarIdentifier' } & Pick<
        Types.ConfiguredCarIdentifier,
        'exteriorColor' | 'interiorColor' | 'salesGroup' | 'equipmentOptions'
      > & {
          model: { __typename?: 'ConfiguredCarModelIdentifier' } & Pick<
            Types.ConfiguredCarModelIdentifier,
            'code' | 'version' | 'year' | 'extensions'
          >;
        };
      carlineIdName?: Types.Maybe<
        { __typename?: 'ConfiguredCarCarline' } & Pick<Types.ConfiguredCarCarline, 'id'>
      >;
      carlineStructureCarline?: Types.Maybe<
        { __typename?: 'CarlineStructureCarline' } & Pick<
          Types.CarlineStructureCarline,
          'vehicleType'
        > & {
            prices: { __typename?: 'CarlineTrimlineFinanceablePrices' } & {
              minPrice?: Types.Maybe<
                { __typename?: 'Price' } & Pick<Types.Price, 'formattedValue'>
              >;
            };
          }
      >;
      technicalData?: Types.Maybe<
        { __typename?: 'ConfiguredCarTechnicalData' } & {
          consumptionAndEmission?: Types.Maybe<
            { __typename?: 'TechnicalDataConsumptionAndEmission' } & {
              summary?: Types.Maybe<
                { __typename?: 'TechnicalDataConsumptionAndEmissionSummary' } & {
                  consumptionsSummary?: Types.Maybe<
                    Array<
                      { __typename?: 'FuelValues' } & {
                        values?: Types.Maybe<
                          Array<
                            {
                              __typename?: 'ConsumptionAndEmissionValuePerEnergyManagementWltp';
                            } & Pick<
                              Types.ConsumptionAndEmissionValuePerEnergyManagementWltp,
                              'formattedValue'
                            >
                          >
                        >;
                      }
                    >
                  >;
                }
              >;
            }
          >;
        }
      >;
      media?: Types.Maybe<
        { __typename?: 'ConfiguredCarMediaResources' } & {
          images: Array<
            { __typename?: 'RenderedCarImage' } & Pick<
              Types.RenderedCarImage,
              'url' | 'profileId' | 'altTextMetaKeys'
            >
          >;
          ave?: Types.Maybe<
            { __typename?: 'ConfiguredCarAve' } & Pick<Types.ConfiguredCarAve, 'renderString'>
          >;
        }
      >;
      catalog?: Types.Maybe<
        { __typename?: 'ConfiguredCarCatalog' } & {
          features: { __typename?: 'ConfiguredCarFeatures' } & {
            exteriorColors: Array<
              { __typename?: 'ConfiguredCarFeature' } & Pick<
                Types.ConfiguredCarFeature,
                'name' | 'pr3' | 'pr7'
              > & {
                  status: { __typename?: 'Status' } & Pick<Types.Status, 'selected'>;
                  imageSets?: Types.Maybe<
                    Array<
                      { __typename?: 'TypedConfiguredCarCatalogImages' } & {
                        images: Array<
                          Types.Maybe<
                            { __typename?: 'ConfiguredCarCatalogImage' } & Pick<
                              Types.ConfiguredCarCatalogImage,
                              'url' | 'width' | 'height'
                            >
                          >
                        >;
                      }
                    >
                  >;
                }
            >;
            rims: Array<
              { __typename?: 'ConfiguredCarFeature' } & Pick<
                Types.ConfiguredCarFeature,
                'name' | 'pr3' | 'pr7'
              > & {
                  status: { __typename?: 'Status' } & Pick<Types.Status, 'selected'>;
                  imageSets?: Types.Maybe<
                    Array<
                      { __typename?: 'TypedConfiguredCarCatalogImages' } & {
                        images: Array<
                          Types.Maybe<
                            { __typename?: 'ConfiguredCarCatalogImage' } & Pick<
                              Types.ConfiguredCarCatalogImage,
                              'url' | 'width' | 'height'
                            >
                          >
                        >;
                      }
                    >
                  >;
                }
            >;
          };
        }
      >;
    };
  carline: { __typename?: 'CarlineStructureCarline' } & Pick<
    Types.CarlineStructureCarline,
    'name'
  > & {
      links?: Types.Maybe<
        { __typename?: 'CarlineStructureLink' } & {
          carlineStartPage?: Types.Maybe<
            { __typename?: 'CarlineLink' } & Pick<Types.CarlineLink, 'url'>
          >;
          configuratorPage?: Types.Maybe<
            { __typename?: 'CarlineLink' } & Pick<Types.CarlineLink, 'url'>
          >;
        }
      >;
      prices: { __typename?: 'CarlineTrimlineFinanceablePrices' } & {
        minPrice?: Types.Maybe<{ __typename?: 'Price' } & Pick<Types.Price, 'value'>>;
      };
      consumptionAndEmission: { __typename?: 'CarlineConsumptionAndEmission' } & {
        summary?: Types.Maybe<
          { __typename?: 'CarlineConsumptionAndEmissionSummary' } & Pick<
            Types.CarlineConsumptionAndEmissionSummary,
            | 'consumption'
            | 'emissionCO2'
            | 'dischargedCO2Class'
            | 'dischargedConsumption'
            | 'co2Class'
          >
        >;
      };
    };
};

export const CustomizerMainDocument = gql`
  query CustomizerMain(
    $carlineInput: ConfiguredCarByCarlineInput!
    $identifier: CarlineIdentifierInput!
  ) {
    configuredCarByCarline(carlineInput: $carlineInput) {
      idString
      id {
        model {
          code
          version
          year
          extensions
        }
        exteriorColor
        interiorColor
        salesGroup
        equipmentOptions
      }
      carlineIdName: carline {
        id
      }
      carlineStructureCarline {
        prices {
          minPrice {
            formattedValue
          }
        }
        vehicleType
      }
      technicalData {
        consumptionAndEmission {
          summary {
            consumptionsSummary {
              values {
                formattedValue
              }
            }
          }
        }
      }
      media {
        images(
          filterInput: {
            filterByProfileId: [
              "EXTERIOR-FRONT-3-4"
              "EXTERIOR-FRONT-3-4-ENV1"
              "EXTERIOR-FRONT-FRONT"
              "EXTERIOR-FRONT-FRONT-ENV1"
              "EXTERIOR-REAR-3-4"
              "EXTERIOR-REAR-3-4-ENV1"
              "EXTERIOR-PARALLEL-SIDE"
              "EXTERIOR-PARALLEL-SIDE-ENV1"
              "EXTERIOR-WHEELS-RIM"
              "EXTERIOR-WHEELS-RIM-ENV1"
              "INTERIOR-DASHBOARD-DASHBOARD"
              "INTERIOR-DASHBOARD-DASHBOARD-ENV1"
              "custfront"
              "custside"
              "cust34front"
              "cust34rear"
            ]
          }
        ) {
          url
          profileId
          altTextMetaKeys
        }
        ave {
          renderString
        }
      }
      catalog {
        features {
          exteriorColors: data(
            featuresFilterInput: {
              filterByFeatureState: [available]
              filterByFeatureGroups: { featureGroups: EXTERIOR_COLORS }
              filterByFamilyIds: { familyIds: ["color-type:exclusive"], mode: EXCLUSIVE }
            }
          ) {
            name
            pr3
            pr7
            status {
              selected
            }
            imageSets(filter: { types: ["outer_tile_button"] }) {
              images {
                url
                width
                height
              }
            }
          }
          rims: data(
            featuresFilterInput: {
              filterByFeatureState: [available]
              filterByFeatureGroups: { featureGroups: RIMS }
            }
          ) {
            name
            pr3
            pr7
            status {
              selected
            }
            imageSets(filter: { types: ["wheel_image_small"] }) {
              images {
                url
                width
                height
              }
            }
          }
        }
      }
    }
    carline(identifier: $identifier) {
      name
      links {
        carlineStartPage {
          url
        }
        configuratorPage {
          url
        }
      }
      prices {
        minPrice {
          value
        }
      }
      consumptionAndEmission {
        summary {
          consumption
          emissionCO2
          dischargedCO2Class
          dischargedConsumption
          co2Class
        }
      }
    }
  }
`;
export type CustomizerMainQueryResult = Apollo.QueryResult<
  CustomizerMainQuery,
  CustomizerMainQueryVariables
>;
